import { isSafeCssColor } from './appearanceSettings';

// Clé localStorage partagée (préférence utilisateur, par appareil/navigateur)
// et nom de l'événement custom dispatché quand elle change — voir
// SideDrawer1.js (toggle) et ProductGridPage/Shop/Search.jsx (écouteurs).
export const CARD_MODE_STORAGE_KEY = 'elakay_grid_card_mode';
export const CARD_MODE_EVENT = 'elakay-card-mode-change';

export const CARD_DISPLAY_SETTINGS_DEFAULTS = Object.freeze({
  defaultMode: 'portrait',
  portrait: Object.freeze({
    showImage: true,
    showTitle: true,
    showPrice: true,
    showDescription: false,
    showButtons: true,
    showRating: true,
    showSeller: false,
    showCategory: false,
    showStock: false,
    showDiscount: false,
    // Colonnes par appareil (react-bootstrap <Row xs sm md lg>) — cards
    // étroites en portrait, donc plus de colonnes.
    columns: Object.freeze({ xs: 2, sm: 2, md: 3, lg: 4 }),
  }),
  landscape: Object.freeze({
    showImage: true,
    showTitle: true,
    showPrice: true,
    showDescription: true,
    showButtons: true,
    showRating: true,
    showSeller: false,
    showCategory: false,
    showStock: false,
    showDiscount: false,
    descriptionMaxCharacters: 120,
    showSeeMore: true,
    seeMoreText: 'See more',
    seeLessText: 'See less',
    descriptionFontSize: '12px',
    descriptionColor: '',
    descriptionLineHeight: '1.4',
    // Cards larges (image + texte côte à côte) → moins de colonnes qu'en
    // portrait pour un meilleur affichage.
    columns: Object.freeze({ xs: 1, sm: 1, md: 2, lg: 2 }),
  }),
});

/**
 * getCardColumns(mode, cardDisplaySettingsFromAdmin, localOverrides)
 *
 * Priorité : localOverrides (prop columns explicite passée par l'appelant)
 * > réglages admin (AnimationSettings → Card Display Mode) > défauts.
 */
export const getCardColumns = (mode, adminSettings, localOverrides) => {
  const resolvedMode = mode === 'landscape' ? 'landscape' : 'portrait';
  const defaults = CARD_DISPLAY_SETTINGS_DEFAULTS[resolvedMode].columns;
  const admin = adminSettings?.[resolvedMode]?.columns || {};
  return { ...defaults, ...admin, ...(localOverrides || {}) };
};

/**
 * resolveActiveCardMode
 *
 * Portrait/Landscape ne s'applique JAMAIS en dehors des pages Grid : tant
 * que isGridPage n'est pas explicitement true, on ignore displayMode,
 * localStorage et le défaut admin — un Card rendu ailleurs (ComponentRegistry,
 * pages non-grid) reste toujours en portrait, même si l'utilisateur a basculé
 * en landscape sur une page grid (la clé localStorage est globale).
 */
export const resolveActiveCardMode = ({
  isGridPage = false,
  displayMode,
  forceLandscape = false,
  defaultModeFromSettings,
} = {}) => {
  if (!isGridPage) return 'portrait';
  if (forceLandscape) return 'landscape';

  const storedMode = typeof window !== 'undefined' && window.localStorage
    ? window.localStorage.getItem(CARD_MODE_STORAGE_KEY)
    : null;

  return displayMode || storedMode || defaultModeFromSettings || 'portrait';
};

const mergeVisibility = (mode, adminSettings, localOverrides) => {
  const defaults = CARD_DISPLAY_SETTINGS_DEFAULTS[mode];
  const admin = adminSettings?.[mode] || {};
  const merged = { ...defaults, ...admin };

  Object.keys(localOverrides || {}).forEach((key) => {
    if (localOverrides[key] !== undefined) merged[key] = localOverrides[key];
  });

  return merged;
};

/**
 * getCardVisibility(mode, cardDisplaySettingsFromAdmin, localOverrides)
 *
 * Priorité : localOverrides (prop cardDataVisibility explicite) > réglages
 * admin (AnimationSettings → Card Display Mode) > défauts codés en dur.
 */
export const getCardVisibility = (mode, adminSettings, localOverrides) => (
  mergeVisibility(mode === 'landscape' ? 'landscape' : 'portrait', adminSettings, localOverrides)
);

export const getLandscapeDescriptionStyle = (visibility) => {
  const style = {};
  if (visibility.descriptionFontSize) style.fontSize = visibility.descriptionFontSize;
  if (visibility.descriptionLineHeight) style.lineHeight = visibility.descriptionLineHeight;
  if (visibility.descriptionColor && isSafeCssColor(visibility.descriptionColor)) {
    style.color = visibility.descriptionColor;
  }
  return style;
};
